import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { useCurrency } from '../contexts/CurrencyContext';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Search, TrendingUp, DollarSign, Pencil, Trash2, Clock, User, Building2, GripVertical, X } from 'lucide-react';
import axios from 'axios';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
import ModuleAutomationsPanel from '../components/automation/ModuleAutomationsPanel';

const API = process.env.REACT_APP_BACKEND_URL;

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(val || 0);
}

const STAGES = [
  { value: 'discovery', label: 'Discovery', color: 'bg-blue-100 text-blue-700 border-blue-300' },
  { value: 'qualification', label: 'Qualification', color: 'bg-cyan-100 text-cyan-700 border-cyan-300' },
  { value: 'proposal', label: 'Proposal', color: 'bg-purple-100 text-purple-700 border-purple-300' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-amber-100 text-amber-700 border-amber-300' },
  { value: 'closed_won', label: 'Closed Won', color: 'bg-green-100 text-green-700 border-green-300' },
  { value: 'closed_lost', label: 'Closed Lost', color: 'bg-red-100 text-red-700 border-red-300' },
];

function DealModal({ open, onClose, onSaved, editing }) {
  const { symbol: currSymbol } = useCurrency();
  const [form, setForm] = useState({ title: '', company: '', contact_name: '', contact_email: '', value: 0, stage: 'discovery', probability: 20, expected_close: '', assigned_to: '', proposal_id: '', notes: '' });
  const [saving, setSaving] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [staff, setStaff] = useState([]);

  useEffect(() => {
    if (open) {
      axios.get(`${API}/api/leads`).then(r => setContacts(r.data?.leads || r.data?.contacts || r.data || [])).catch(() => {});
      axios.get(`${API}/api/clients/dropdown`).then(r => setClients(r.data || [])).catch(() => {});
      axios.get(`${API}/api/staff/members`).then(r => setStaff(r.data?.staff || [])).catch(() => {});
      axios.get(`${API}/api/sales/proposals`).then(r => setProposals(r.data || [])).catch(() => {});
    }
  }, [open]);
  const [clients, setClients] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [sourceType, setSourceType] = useState('lead');

  useEffect(() => {
    if (editing) setForm({ title: editing.title || '', company: editing.company || '', contact_name: editing.contact_name || '', contact_email: editing.contact_email || '', value: editing.value || 0, stage: editing.stage || 'discovery', probability: editing.probability || 20, expected_close: editing.expected_close || '', assigned_to: editing.assigned_to || '', proposal_id: editing.proposal_id || '', notes: editing.notes || '' });
    else setForm({ title: '', company: '', contact_name: '', contact_email: '', value: 0, stage: 'discovery', probability: 20, expected_close: '', assigned_to: '', proposal_id: '', notes: '' });
  }, [editing, open]);

  const handleContactSelect = (contactId) => {
    if (sourceType === 'client') {
      const c = clients.find(c => c.id === contactId);
      if (c) setForm(p => ({
        ...p,
        contact_name: c.contact_name || '',
        contact_email: c.email || '',
        company: c.company_name || p.company,
        title: p.title || `Deal - ${c.contact_name || 'Client'}`,
      }));
    } else {
      const c = contacts.find(c => (c.id || c._id || c.lead_id) === contactId);
      if (c) setForm(p => ({
        ...p,
        contact_name: c.full_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        contact_email: c.email || '',
        company: c.company || c.business_name || p.company,
        title: p.title || `Deal - ${c.full_name || c.name || c.first_name || 'Contact'}`,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setSaving(true);
    try {
      const { data } = editing
        ? await axios.put(`${API}/api/sales/deals/${editing.id}`, form)
        : await axios.post(`${API}/api/sales/deals`, form);
      toast.success(editing ? 'Deal updated' : 'Deal created');
      onSaved(data, !!editing); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto"><DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="h-5 w-5 text-purple-500" />{editing ? 'Edit' : 'New'} Deal</DialogTitle><DialogDescription>Track a sales opportunity</DialogDescription></DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3 mt-2">
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs font-semibold">Link to:</Label>
              <Button type="button" size="sm" variant={sourceType === 'lead' ? 'default' : 'outline'} className={`h-6 text-[10px] ${sourceType === 'lead' ? 'bg-blue-600' : ''}`} onClick={() => setSourceType('lead')}>Lead</Button>
              <Button type="button" size="sm" variant={sourceType === 'client' ? 'default' : 'outline'} className={`h-6 text-[10px] ${sourceType === 'client' ? 'bg-emerald-600' : ''}`} onClick={() => setSourceType('client')}>Client</Button>
            </div>
            {sourceType === 'lead' && contacts.length > 0 && (
              <Select onValueChange={handleContactSelect}>
                <SelectTrigger data-testid="deal-contact-select"><SelectValue placeholder="Select a lead to auto-fill..." /></SelectTrigger>
                <SelectContent className="z-[10000]">
                  {contacts.filter(c => (c.id || c._id || c.lead_id)).map(c => {
                    const cId = String(c.id || c._id || c.lead_id);
                    const cName = c.full_name || c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim() || c.email || 'Unknown';
                    return <SelectItem key={cId} value={cId}>{cName} {c.company ? `(${c.company})` : ''} {c.email ? `— ${c.email}` : ''}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            )}
            {sourceType === 'client' && clients.length > 0 && (
              <Select onValueChange={handleContactSelect}>
                <SelectTrigger data-testid="deal-client-select"><SelectValue placeholder="Select a client to auto-fill..." /></SelectTrigger>
                <SelectContent className="z-[10000]">
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.contact_name} {c.company_name ? `(${c.company_name})` : ''} {c.email ? `— ${c.email}` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
          </div>
          <div><Label>Deal Title *</Label><Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} data-testid="deal-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Company</Label><Input value={form.company} onChange={e => setForm(p => ({...p, company: e.target.value}))} data-testid="deal-company" /></div>
            <div><Label>Contact</Label><Input value={form.contact_name} onChange={e => setForm(p => ({...p, contact_name: e.target.value}))} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Value ({currSymbol})</Label><Input type="number" value={form.value} onChange={e => setForm(p => ({...p, value: parseFloat(e.target.value) || 0}))} data-testid="deal-value" /></div>
            <div><Label>Stage</Label><Select value={form.stage} onValueChange={v => setForm(p => ({...p, stage: v}))}><SelectTrigger data-testid="deal-stage"><SelectValue /></SelectTrigger><SelectContent>{STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Probability (%)</Label><Input type="number" min="0" max="100" value={form.probability} onChange={e => setForm(p => ({...p, probability: parseInt(e.target.value) || 0}))} /></div>
            <div><Label>Expected Close</Label><Input type="date" value={form.expected_close} onChange={e => setForm(p => ({...p, expected_close: e.target.value}))} /></div>
          </div>
          <div><Label>Assigned To</Label>
            {staff.length > 0 ? (
              <Select value={form.assigned_to || 'unassigned'} onValueChange={v => setForm(p => ({...p, assigned_to: v === 'unassigned' ? '' : v}))}>
                <SelectTrigger><SelectValue placeholder="Select team member" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">Unassigned</SelectItem>
                  {staff.filter(s => s.id).map(s => <SelectItem key={s.id} value={`${s.first_name} ${s.last_name}`.trim() || s.email}>{s.first_name} {s.last_name} {s.job_title ? `(${s.job_title})` : ''}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Input value={form.assigned_to} onChange={e => setForm(p => ({...p, assigned_to: e.target.value}))} />
            )}
          </div>
          {/* Link to Proposal/Quote */}
          {proposals.length > 0 && (
            <div>
              <Label>Link to Proposal/Quote</Label>
              <Select value={form.proposal_id || 'none'} onValueChange={v => {
                const pid = v === 'none' ? '' : v;
                setForm(p => ({ ...p, proposal_id: pid }));
                if (pid) {
                  const prop = proposals.find(pr => pr.id === pid);
                  if (prop) {
                    setForm(p => ({
                      ...p,
                      proposal_id: pid,
                      title: p.title || `Deal - ${prop.title}`,
                      company: p.company || prop.client_company || '',
                      contact_name: p.contact_name || prop.client_name || '',
                      contact_email: p.contact_email || prop.client_email || '',
                      value: prop.subtotal || p.value,
                    }));
                  }
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Link a proposal..." /></SelectTrigger>
                <SelectContent className="z-[10000]">
                  <SelectItem value="none">No linked proposal</SelectItem>
                  {proposals.map(pr => <SelectItem key={pr.id} value={pr.id}>{pr.title} ({pr.doc_type || 'proposal'}) — {formatCurrency(pr.subtotal)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2} /></div>
          <DialogFooter><Button type="button" variant="outline" onClick={onClose}>Cancel</Button><Button type="submit" disabled={saving} className="bg-purple-600 hover:bg-purple-700" data-testid="save-deal-btn">{saving ? 'Saving...' : editing ? 'Update' : 'Create Deal'}</Button></DialogFooter>
        </form></DialogContent></Dialog>
  );
}

export default function SalesPipelinePage() {
  const { format: fc } = useCurrency();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState(() => window.innerWidth < 640 ? 'list' : 'board');

  const fetchDeals = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/sales/deals`);
      setDeals(data);
    } catch { toast.error('Failed to load deals'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  const handleSaved = (d, isUpdate) => { if (isUpdate) setDeals(p => p.map(x => x.id === d.id ? d : x)); else setDeals(p => [d, ...p]); };
  const handleDelete = async (id) => { if (!window.confirm('Delete deal?')) return; try { await axios.delete(`${API}/api/sales/deals/${id}`); setDeals(p => p.filter(d => d.id !== id)); toast.success('Deleted'); } catch { toast.error('Failed'); } };
  const moveStage = async (dealId, newStage) => {
    try {
      const { data } = await axios.put(`${API}/api/sales/deals/${dealId}`, { stage: newStage });
      setDeals(p => p.map(d => d.id === dealId ? data : d));
      toast.success(`Moved to ${STAGES.find(s => s.value === newStage)?.label}`);
    } catch { toast.error('Failed'); }
  };

  const totalValue = deals.reduce((s, d) => s + (d.value || 0), 0);
  const wonValue = deals.filter(d => d.stage === 'closed_won').reduce((s, d) => s + (d.value || 0), 0);

  return (
    <div className="space-y-4 animate-fadeIn" data-testid="sales-pipeline-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3 flex-1"><div className="w-10 h-10 rounded-xl bg-purple-50 flex items-center justify-center"><TrendingUp className="h-5 w-5 text-purple-500" /></div><div><h1 className="text-xl sm:text-2xl font-heading font-bold text-text-primary">Sales Pipeline<PageInfoTooltip pageKey="sales-pipeline" /></h1><p className="text-xs text-gray-400">{deals.length} deals · {fc(totalValue)} pipeline · {fc(wonValue)} won</p></div></div>
        <div className="flex items-center gap-2">
          <Button variant={view === 'board' ? 'default' : 'outline'} size="sm" onClick={() => setView('board')} className={view === 'board' ? 'bg-purple-600' : ''}>Board</Button>
          <Button variant={view === 'list' ? 'default' : 'outline'} size="sm" onClick={() => setView('list')} className={view === 'list' ? 'bg-purple-600' : ''}>List</Button>
          <Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-purple-600 hover:bg-purple-700 h-9 gap-1.5" data-testid="new-deal-btn"><Plus className="h-4 w-4" /> New Deal</Button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><div className="w-8 h-8 rounded-full border-2 border-purple-500 border-t-transparent animate-spin" /></div> : view === 'board' ? (
        <div className="flex gap-3 overflow-x-auto pb-4 -mx-1 px-1" data-testid="pipeline-board">
          {STAGES.map(stage => {
            const stageDeals = deals.filter(d => d.stage === stage.value);
            const stageValue = stageDeals.reduce((s, d) => s + (d.value || 0), 0);
            return (
              <div key={stage.value} className="min-w-[220px] sm:min-w-[260px] w-[220px] sm:w-[260px] flex-shrink-0" data-testid={`stage-col-${stage.value}`}>
                <div className={`rounded-t-xl px-3 py-2 border ${stage.color}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-bold">{stage.label}</span>
                    <Badge variant="outline" className="text-[9px]">{stageDeals.length}</Badge>
                  </div>
                  <p className="text-[10px] opacity-70">{fc(stageValue)}</p>
                </div>
                <div className="bg-gray-50 rounded-b-xl border border-t-0 border-gray-200 min-h-[200px] p-2 space-y-2">
                  {stageDeals.map(deal => (
                    <Card key={deal.id} className="border-gray-200 hover:shadow-sm transition-shadow cursor-pointer" data-testid={`deal-card-${deal.id}`}>
                      <CardContent className="p-3">
                        <h4 className="text-sm font-semibold text-gray-900 line-clamp-1">{deal.title}</h4>
                        {deal.company && <p className="text-[10px] text-gray-500 flex items-center gap-0.5 mt-0.5"><Building2 className="h-2.5 w-2.5" />{deal.company}</p>}
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-sm font-bold text-gray-800">{fc(deal.value || 0)}</span>
                          <span className="text-[10px] text-gray-400">{deal.probability}%</span>
                        </div>
                        <div className="flex gap-1 mt-2 justify-end">
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => { setEditing(deal); setModalOpen(true); }}><Pencil className="h-3 w-3 text-gray-400" /></Button>
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleDelete(deal.id)}><Trash2 className="h-3 w-3 text-red-400" /></Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                  {stageDeals.length === 0 && <p className="text-[10px] text-gray-300 text-center py-6">No deals</p>}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2">
          {deals.length === 0 ? (
            <Card className="border-dashed border-2 border-gray-300"><CardContent className="p-10 text-center"><TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" /><h3 className="text-lg font-semibold text-gray-700 mb-1">No deals</h3><Button onClick={() => { setEditing(null); setModalOpen(true); }} className="bg-purple-600 hover:bg-purple-700 gap-1.5 mt-3"><Plus className="h-4 w-4" /> Create Deal</Button></CardContent></Card>
          ) : deals.map(d => {
            const stageInfo = STAGES.find(s => s.value === d.stage) || STAGES[0];
            return (
              <Card key={d.id} className="border-gray-200 hover:shadow-sm transition-shadow" data-testid={`deal-row-${d.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 min-w-0"><h3 className="text-sm font-semibold text-gray-900 truncate">{d.title}</h3><p className="text-[10px] text-gray-400 truncate">{d.company} · {d.contact_name}</p></div>
                    <Badge className={`${stageInfo.color} text-[9px] border flex-shrink-0`}>{stageInfo.label}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-sm font-bold text-gray-800">{fc(d.value || 0)}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400">{d.probability}%</span>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(d); setModalOpen(true); }}><Pencil className="h-3.5 w-3.5 text-gray-400" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDelete(d.id)}><Trash2 className="h-3.5 w-3.5 text-red-400" /></Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <DealModal open={modalOpen} onClose={() => { setModalOpen(false); setEditing(null); }} onSaved={handleSaved} editing={editing} />

      {/* Per-module automation panel — configure sales automations right here */}
      <ModuleAutomationsPanel module="sales" title="Sales Automations" />
    </div>
  );
}
